import { useEffect, useState } from "react";
import {
  FacetCardinality,
  useCreateSiteFacetMutation,
} from "../../../../redux/api/site-classification/siteClassificationAPI";
import { IHttpError } from "../../../../shared/oversight-core/interfaces/http-errror";
import AppSelect, {
  Option,
} from "../../../../shared/oversight-core/ui-elements/app-select/app-select";
import CheckBox from "../../../../shared/oversight-core/ui-elements/check-box/check-box";
import AppInput from "../../../../shared/oversight-core/ui-elements/input/app-input";
import ModalContainer from "../../../../shared/oversight-core/ui-elements/modal-container/modal-container";
import {
  showErrorMessage,
  showSuccessMessage,
} from "../../../../shared/oversight-core/utils/toast";

interface IProps {
  show: boolean;
  tenantId: string;
  onClose: () => void;
}

const cardinalityOptions: Option[] = [
  { value: "SINGLE", label: "One value per site" },
  { value: "MULTI", label: "Several values per site" },
];

// Adds a facet of the organization's own. Whether a site takes one value or several is fixed
// once the facet exists, since sites are then tagged by it.
const AddSiteFacetModal = (props: IProps) => {
  const { show, tenantId, onClose } = props;
  const [name, setName] = useState("");
  const [cardinality, setCardinality] = useState<FacetCardinality>("MULTI");
  const [hiddenFromGuests, setHiddenFromGuests] = useState(false);
  const [createSiteFacet, { isLoading }] = useCreateSiteFacetMutation();

  useEffect(() => {
    if (show) {
      setName("");
      setCardinality("MULTI");
      setHiddenFromGuests(false);
    }
  }, [show]);

  const create = () => {
    createSiteFacet({ tenantId, name, cardinality, hiddenFromGuests })
      .unwrap()
      .then(() => {
        showSuccessMessage(`${name.trim()} added`);
        onClose();
      })
      .catch((error: IHttpError) =>
        showErrorMessage(error?.message || "The facet could not be added."),
      );
  };

  return (
    <ModalContainer
      show={show}
      onClose={onClose}
      onCancel={onClose}
      onConfirm={create}
      title="Add facet"
      confirmButtonText="Add"
      confirmButtonVariant="blue"
      size="modal-md"
      isLoading={isLoading}
      disabled={name.trim() === ""}
    >
      <div className="d-flex flex-column gap-3">
        <AppInput
          name="facetName"
          label="Name"
          placeholder="e.g. Program"
          value={name}
          onChange={(event) => setName(event.target.value)}
          errors={{}}
        />
        <AppSelect
          label="Values per site"
          options={cardinalityOptions}
          selectedValue={cardinalityOptions.find(
            (option) => option.value === cardinality,
          )}
          onChangeOption={(option) =>
            setCardinality(option.value as FacetCardinality)
          }
          isRequired={false}
        />
        <div className="text-light font-size-12">
          This cannot be changed once the facet exists.
        </div>
        <CheckBox
          label="Hide from monitoring guests"
          checked={hiddenFromGuests}
          onChange={setHiddenFromGuests}
        />
      </div>
    </ModalContainer>
  );
};

export default AddSiteFacetModal;
